import styled, { css } from 'styled-components';
import { SIDEBAR_MOBILE_BREAKPOINT } from '@constants/sidebar';

const MOBILE = `@media (max-width: ${SIDEBAR_MOBILE_BREAKPOINT}px)`;

/**
 * Outer list — vertical stack of process rows. Reset default `<ul>` styling
 * (no bullets, no padding) and add a small consistent gap between items so
 * each row reads as its own card rather than a contiguous table.
 */
export const ListRoot = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

/**
 * Holds `RowMain` and the optional `DetailsPanel` stacked vertically.
 * When expanded, the gap collapses so the row and details panel fuse into
 * a single visual container.
 */
export const ListItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

/**
 * The clickable card portion of a process row. Matches the design's rounded
 * (24px) row with no resting background — chrome appears only on hover
 * (surface fill + subtle outline ring + drop shadow).
 *
 * When expanded (`data-expanded`), the row fuses visually with the details
 * panel below: shared background, bottom radii removed, hover disabled.
 */
export const RowMain = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme: { spacing } }) => spacing?.level5 ?? '16px'};
  padding: 12px 16px;
  border-radius: 24px;
  cursor: pointer;
  outline: none;
  transition:
    background ${({ theme: { motion } }) => motion?.duration?.fast ?? '120ms'}
      ${({ theme: { motion } }) => motion?.easing?.standard ?? 'ease'},
    box-shadow 140ms ease;

  ${MOBILE} {
    flex-wrap: wrap;
    row-gap: 8px;
  }

  ${({ theme: { colors = {} } }) => {
    const surfaceContainerLow =
      colors.surfaceContainerLow ?? '#13141E';
    const surfaceContainer = colors.surfaceContainer ?? '#181927';
    const primary = colors.primary ?? '#C1C1FF';

    return css`
      background: transparent;

      &:hover {
        background: ${surfaceContainerLow};
        box-shadow:
          inset 0 0 0 1px #676680,
          0 4px 14px rgba(0, 0, 0, 0.18);
      }

      &:focus-visible {
        background: ${surfaceContainerLow};
        box-shadow:
          0 0 0 2px ${primary},
          0 4px 14px rgba(0, 0, 0, 0.18);
      }

      &[data-expanded] {
        background: ${surfaceContainer};
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        box-shadow: none;
      }

      &[data-expanded]:hover {
        box-shadow: none;
      }

      &[data-expanded]:focus-visible {
        box-shadow: 0 0 0 2px ${primary};
      }
    `;
  }}
`;

/**
 * Square 40x40 icon tile rendered to the left of the process name. Transparent
 * background with a muted glyph so it reads as a "row identity" marker without
 * stealing focus from the title.
 */
export const IconTile = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${({ theme: { borderRadius = {} } }) =>
    borderRadius.small ?? '8px'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#A1A3B8'};
  background: transparent;
  flex-shrink: 0;

  & > svg {
    width: 22px;
    height: 22px;
  }
`;

/**
 * Flexible name + meta column. `min-width: 0` is critical so its long content
 * (process name + description) can ellipsize instead of pushing the actions
 * cluster off the right edge of the row.
 */
export const NameStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;

  ${MOBILE} {
    flex-basis: calc(100% - 56px);
  }
`;

export const NameTitle = styled.div`
  font-size: 13.5px;
  font-weight: 600;
  line-height: 1.25;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/**
 * Inline rename editor — replaces `NameTitle` while a row is in rename mode.
 * Sized to match the title visually so the row doesn't reflow as it swaps in.
 * Focus ring is the M3 primary so it reads as the active edit target.
 */
export const NameRenameInput = styled.input`
  font: inherit;
  font-size: 13.5px;
  font-weight: 600;
  line-height: 1.25;
  width: 100%;
  padding: 4px 8px;
  margin: -4px -8px;
  border-radius: 6px;
  outline: none;
  ${({ theme: { colors = {} } }) => css`
    background: ${colors.surfaceContainerHigh ?? '#1F2030'};
    color: ${colors.onSurface ?? '#E6E6F2'};
    border: 1px solid ${colors.primary ?? '#C1C1FF'};
    box-shadow: 0 0 0 2px
      color-mix(in oklch, ${colors.primary ?? '#C1C1FF'} 25%, transparent);
  `}
`;

/**
 * Inline meta row beneath the title: owner mini-avatar + name · "Updated X".
 * Wraps gracefully on narrow viewports.
 */
export const MetaLine = styled.div`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 11.5px;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#A1A3B8'};
`;

export const MetaOwner = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
`;

export const MetaSep = styled.span`
  opacity: 0.5;
`;

export const MetaUpdated = styled.span`
  font-variant-numeric: tabular-nums;
`;

/**
 * Right-aligned actions cluster: status pill, primary action button (Run/Open),
 * expand toggle, and the kebab menu — separated by consistent gaps.
 */
export const RowActionsEnd = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme: { spacing } }) => spacing?.level4 ?? '10px'};
  flex-shrink: 0;

  ${MOBILE} {
    flex-basis: 100%;
  }
`;

/**
 * Expand-details panel — sits directly below the row when expanded. Fuses
 * visually with the row above: shared surface background, no separate border,
 * only bottom corners rounded — creating one continuous card.
 */
export const DetailsPanel = styled.div`
  padding: 14px 18px 16px;
  background: ${({ theme: { colors = {} } }) =>
    colors.surfaceContainer ?? '#181927'};
  border: 0;
  border-radius: 0 0 10px 10px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const DetailsDescription = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
  max-width: 720px;
`;

export const DetailsStatsGrid = styled.dl`
  margin: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 14px 32px;
  align-items: start;
`;

export const DetailsStat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

export const DetailsStatLabel = styled.dt`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#A1A3B8'};
`;

export const DetailsStatValue = styled.dd`
  margin: 0;
  font-size: 13.5px;
  font-weight: 500;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
  font-variant-numeric: tabular-nums;
`;

export const DetailsStatOwner = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13.5px;
  font-weight: 500;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
`;
